import CircleAndMemberFilters from '@/circle/components/CircleAndMemberFilters'
import ScrollableLayout from '@/common/atoms/ScrollableLayout'
import { Title } from '@/common/atoms/Title'
import useUpdatableQueryParams from '@/common/hooks/useUpdatableQueryParams'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react'
import { Next_Step_Bool_Exp, useNextStepsSubscription } from '@gql'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from 'src/icons'
import NextStepsList, { NextStepsStatus } from '../components/NextStepsList'

type Params = {
  member: string
  circle: string
  status: string
}

// Three exclusive views, no "all" (ENR07-D3, D4). They replace both the folded
// "Réalisés" section and the "show archived" toggle.
const statusList: NextStepsStatus[] = ['todo', 'done', 'archived']

export default function NextStepsPage() {
  const { t } = useTranslation()
  const { orgId, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const { params, changeParams } = useUpdatableQueryParams<Params>()

  const memberId =
    params.member && typeof params.member === 'string'
      ? params.member
      : undefined
  const circleId =
    params.circle && typeof params.circle === 'string'
      ? params.circle
      : undefined
  // Opens on "à faire": the page shows what is still owed, and the balance of
  // what one has closed costs a click (ENR07-D5). That is the assumed price of
  // the menu, and the same default as the Tensions page.
  const status: NextStepsStatus =
    typeof params.status === 'string' &&
    statusList.includes(params.status as NextStepsStatus)
      ? (params.status as NextStepsStatus)
      : 'todo'

  const handleCircleChange = (circle: string | undefined) =>
    changeParams({ circle })
  const handleMemberChange = (member: string | undefined) =>
    changeParams({ member })

  // My roles: the circles I am a direct member of.
  const myCirclesIds = useMemo(
    () =>
      currentMember
        ? orgData?.circlesOfMember(currentMember.id).map((c) => c.id) ?? []
        : [],
    [orgData, currentMember?.id]
  )

  // The filter is built here and handed to a generic subscription, so adding a
  // criterion never costs a codegen round trip.
  //
  // ⚠️ Hasura's permissions apply ON TOP of this: a wider filter never shows
  // anything more. It sorts what one is allowed to see, it does not open it.
  const where = useMemo<Next_Step_Bool_Exp>(() => {
    // The view becomes a filter, built here in the bool_exp the generic
    // subscription already takes — so a new view never costs a codegen round.
    const and: Next_Step_Bool_Exp[] = [
      { orgId: { _eq: orgId } },
      status === 'archived'
        ? { archivedAt: { _is_null: false } }
        : { archivedAt: { _is_null: true }, done: { _eq: status === 'done' } },
    ]

    if (circleId) and.push({ circleId: { _eq: circleId } })
    if (memberId) and.push({ memberId: { _eq: memberId } })

    // Default view: my roles AND what is assigned to me, together (G3-D5).
    // The holder must know what is being done in their name, even by someone
    // else — opening on "assigned to me" alone would hide exactly what D11
    // wanted to make visible.
    if (!circleId && !memberId && currentMember) {
      and.push({
        _or: [
          { circleId: { _in: myCirclesIds } },
          { memberId: { _eq: currentMember.id } },
        ],
      })
    }

    return { _and: and }
  }, [orgId, status, circleId, memberId, currentMember?.id, myCirclesIds])

  const { data } = useNextStepsSubscription({ skip: !orgId, variables: { where } })
  const nextSteps = data?.next_step ?? []

  return (
    <ScrollableLayout
      header={
        // The very layout of ThreadsPage: filters live in the window's top bar,
        // not in the body (ENR07-D1). Heading at size lg, like the two model
        // pages — it was md here, alone in being smaller.
        <Flex ml={5} w="100%" alignItems="center" flexWrap="wrap">
          <Heading as="h1" size="lg">
            {t('NextStepsPage.heading')}
          </Heading>

          {/* The shared component, and not the two bespoke selectors: it is the
              one that turns a button into a search field with a clearing cross,
              which is exactly the gesture asked for (ENR07-D2). Clearing does
              not mean "no filter" here, it means MY default — my roles and what
              is assigned to me (G3-D5). */}
          <CircleAndMemberFilters
            circleId={circleId}
            memberId={memberId}
            ml={5}
            my={2}
            onCircleChange={handleCircleChange}
            onMemberChange={handleMemberChange}
          >
            {/* Inside the shared component, as ThreadsPage does: the three
                controls form a single group, and that is what aligns them. */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon size="1em" />}
                fontWeight="normal"
              >
                {t(`NextStepsPage.status.${status}`)}
              </MenuButton>
              <MenuList zIndex={2000}>
                <MenuOptionGroup
                  type="radio"
                  value={status}
                  onChange={(value) =>
                    typeof value === 'string' &&
                    changeParams({
                      status: value === 'todo' ? undefined : value,
                    })
                  }
                >
                  {statusList.map((value) => (
                    <MenuItemOption key={value} value={value}>
                      {t(`NextStepsPage.status.${value}`)}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </CircleAndMemberFilters>

          {/* No Spacer, no action button: unlike Tensions and Projets, nothing
              is created from this page (G3-D10, ENR07-D6). A next step is born
              in a meeting, and the empty corner says so. */}
        </Flex>
      }
    >
      <Title>{t('NextStepsPage.heading')}</Title>

      <Box p={5}>
        <NextStepsList nextSteps={nextSteps} status={status} />
      </Box>
    </ScrollableLayout>
  )
}
